import { useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { Link } from 'react-router-dom';
import { Formik } from 'formik';
import { ChevronLeft, ChevronRight, HandCoins } from 'lucide-react';
import { FichaService, UsuarioService } from '../../services';
import { ComissaoMes } from '@/services/FichaService';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { IconeTile } from '@/components/ui/cores';
import { PageHeader } from '@/components/ui/page-header';
import { FormikAutoComplete } from '@/components/ui/autocomplete';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from '@/utils/format';
import { getLinkFichasDoMes } from '@/utils/FichaUtils';
import { MesValues } from '@/types/enum/Mes';
import { Usuario } from '@/types/usuario';
import { ErrorPage } from '../ErrorPage';

const soma = (itens: ComissaoMes[], campo: 'quantidade' | 'premioLiquido' | 'comissao') =>
    itens.reduce((total, item) => total + Number(item[campo] || 0), 0);

const Total = ({ titulo, children }: { titulo: string; children: React.ReactNode }) => (
    <Card className="p-5 shadow-none">
        <p className="text-sm text-muted-foreground">{titulo}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">{children}</p>
    </Card>
);

// Comissão prevista dos seguros pelo mês de início da vigência
const RelatorioComissoesPage = () => {
    const [ano, setAno] = useState(new Date().getFullYear());
    const [corretor, setCorretor] = useState<Usuario>();
    const [itens, setItens] = useState<ComissaoMes[]>([]);
    const [error, setError] = useState<AxiosError>();

    useEffect(() => {
        FichaService.findComissoes(ano, corretor?.id).then(({ data }) => setItens(data)).catch(setError);
    }, [ano, corretor]);

    const meses = useMemo(() => MesValues
        .map((mes, i) => ({ numero: i + 1, nome: mes.descricao, itens: itens.filter(item => item.mes === i + 1) }))
        .filter(mes => mes.itens.length > 0), [itens]);

    if (error) {
        return <ErrorPage axiosError={error} />
    }

    return (
        <div className="space-y-6">
            <PageHeader icon={<IconeTile cor="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"><HandCoins /></IconeTile>} title="Comissões">
                <div className="flex items-center rounded-md border">
                    <Button variant="ghost" size="icon" aria-label="Ano anterior" onClick={() => setAno(ano - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                    <span className="w-14 text-center text-sm font-medium tabular-nums">{ano}</span>
                    <Button variant="ghost" size="icon" aria-label="Próximo ano" onClick={() => setAno(ano + 1)}><ChevronRight className="h-4 w-4" /></Button>
                </div>
            </PageHeader>

            <div className="max-w-md">
                <Formik initialValues={{ corretor: undefined }} onSubmit={() => {}}>
                    <FormikAutoComplete
                        onSearch={UsuarioService.autoCompleteAtivo}
                        name="corretor"
                        placeholder="Todos os corretores"
                        getOptionLabel={item => item?.nome}
                        getOptionValue={item => item}
                        getKey={item => item.id}
                        onValueChange={(item: any) => setCorretor(item || undefined)}
                    />
                </Formik>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
                <Total titulo="Comissão prevista">{formatCurrency(soma(itens, 'comissao'))}</Total>
                <Total titulo="Prêmio líquido">{formatCurrency(soma(itens, 'premioLiquido'))}</Total>
                <Total titulo="Seguros iniciados">{soma(itens, 'quantidade').toLocaleString('pt-BR')}</Total>
            </div>

            <Card className="overflow-hidden shadow-none">
                <Table>
                    <TableHeader className="bg-muted/40">
                        <TableRow className="hover:bg-transparent">
                            <TableHead className="h-10 px-4 text-xs">Mês / seguradora</TableHead>
                            <TableHead className="h-10 px-4 text-right text-xs">Seguros</TableHead>
                            <TableHead className="h-10 px-4 text-right text-xs">Prêmio líquido</TableHead>
                            <TableHead className="h-10 px-4 text-right text-xs">Comissão</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {meses.length === 0 && (
                            <TableRow className="hover:bg-transparent">
                                <TableCell colSpan={4} className="h-32 text-center text-sm text-muted-foreground">Nenhum seguro com início de vigência em {ano}.</TableCell>
                            </TableRow>
                        )}
                        {meses.map(mes => [
                            <TableRow key={mes.nome} className="bg-muted/20 font-medium hover:bg-muted/20">
                                <TableCell className="px-4"><Link to={getLinkFichasDoMes(mes.numero, ano, undefined, corretor, 'Vigência Inicial')} className="hover:underline">{mes.nome}</Link></TableCell>
                                <TableCell className="px-4 text-right tabular-nums">{soma(mes.itens, 'quantidade')}</TableCell>
                                <TableCell className="px-4 text-right tabular-nums">{formatCurrency(soma(mes.itens, 'premioLiquido'))}</TableCell>
                                <TableCell className="px-4 text-right tabular-nums">{formatCurrency(soma(mes.itens, 'comissao'))}</TableCell>
                            </TableRow>,
                            ...mes.itens.map(item => (
                                <TableRow key={`${mes.nome}-${item.seguradora}`} className="text-muted-foreground">
                                    <TableCell className="py-2 pl-8 pr-4"><Link to={getLinkFichasDoMes(mes.numero, ano, { nome: item.seguradora }, corretor, 'Vigência Inicial')} className="hover:underline">{item.seguradora}</Link></TableCell>
                                    <TableCell className="px-4 py-2 text-right tabular-nums">{item.quantidade}</TableCell>
                                    <TableCell className="px-4 py-2 text-right tabular-nums">{formatCurrency(item.premioLiquido)}</TableCell>
                                    <TableCell className="px-4 py-2 text-right tabular-nums">{formatCurrency(item.comissao)}</TableCell>
                                </TableRow>
                            ))
                        ])}
                    </TableBody>
                    {meses.length > 0 && (
                        <TableFooter>
                            <TableRow className="font-semibold">
                                <TableCell className="px-4">Total do ano</TableCell>
                                <TableCell className="px-4 text-right tabular-nums">{soma(itens, 'quantidade')}</TableCell>
                                <TableCell className="px-4 text-right tabular-nums">{formatCurrency(soma(itens, 'premioLiquido'))}</TableCell>
                                <TableCell className="px-4 text-right tabular-nums">{formatCurrency(soma(itens, 'comissao'))}</TableCell>
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </Card>
        </div>
    );
}

export default RelatorioComissoesPage;
